import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter, map, pairwise, shareReplay, startWith } from 'rxjs/operators';
import { LANDING_URL } from '../common/utils/url';
import { Session } from '../models/session';
import { SpaceRepository } from '../state/space.repository';
import { UserService } from './user.service';
import { BreakoutRoomsManagementService } from './breakout-rooms/breakout-rooms-management.service';

@Injectable({
  providedIn: 'root',
})
export class NavService {
  /**
   * Whether the global sidenav is open. Single source of truth for the drawer: MatSidenav binds it,
   * writes back on close, and the Spaces Switcher collapse/re-open controls toggle it.
   */
  isMenuOpened = new BehaviorSubject(false);
  headerTitle = new BehaviorSubject('');
  showCalculator = new BehaviorSubject(false);
  showGraph = new BehaviorSubject(false);
  showPeriodicTable = new BehaviorSubject(false);
  showSessionAnalytics = new BehaviorSubject(false);
  sessionAnalyticsSpace = new BehaviorSubject<Session | undefined>(undefined);
  isCalculatorOpen = false;
  isCalculatorFocused = false;
  isGraphOpen = false;
  isPeriodicTableOpen = false;
  isSessionAnalyticsOpen = false;

  /**
   * Emits the current router URL on every navigation (and once with the current URL on
   * subscription). Reusable source for anything that reacts to route changes, replacing
   * ad-hoc `router.events` + `NavigationEnd` filtering scattered across components.
   */
  readonly navigationEndUrl$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.router.url),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  loading: any = new Promise((resolve) => {
    this.userService.user.subscribe((res) => {
      resolve(res?.user);
    });
  });

  constructor(
    private router: Router,
    private userService: UserService,
    private breakoutRoomsManagementService: BreakoutRoomsManagementService,
    private spaceRepo: SpaceRepository,
  ) {
    this.subscribeToHostDemotion();
  }

  /**
   * Navigates to Home.
   */
  goToHomePage(): Promise<boolean> {
    return this.router.navigateByUrl(`/${LANDING_URL}`);
  }

  /**
   * When the current user is demoted from host to participant, close the session analytics view
   * (analytics is a host-only feature in session).
   */
  private subscribeToHostDemotion(): void {
    this.spaceRepo.isCurrentUserHost$
      .pipe(
        startWith(this.spaceRepo.isCurrentUserHost()),
        pairwise(),
        filter(([wasHost, isHost]) => wasHost && !isHost),
      )
      .subscribe(() => {
        if (this.isSessionAnalyticsOpen) {
          this.closeSessionAnalytics();
        }
      });
  }

  closeAllGadgets() {
    if (this.isGraphOpen) {
      this.closeGraph();
    }
    if (this.isCalculatorOpen) {
      this.closeCalculator();
    }
    if (this.isPeriodicTableOpen) {
      this.closePeriodicTable();
    }
    if (this.isSessionAnalyticsOpen) {
      this.closeSessionAnalytics();
    }
  }

  closeGraph() {
    this.showGraph.next(false);
    this.isGraphOpen = false;
  }

  toggleGraph() {
    this.isGraphOpen = !this.isGraphOpen;
    this.showGraph.next(this.isGraphOpen);
  }

  closeCalculator() {
    this.showCalculator.next(false);
    this.isCalculatorOpen = false;
    this.isCalculatorFocused = false;
  }

  toggleCalculator() {
    this.isCalculatorOpen = !this.isCalculatorOpen;
    if (!this.isCalculatorOpen) {
      this.isCalculatorFocused = false;
    }
    this.showCalculator.next(this.isCalculatorOpen);
  }

  closePeriodicTable() {
    this.showPeriodicTable.next(false);
    this.isPeriodicTableOpen = false;
  }

  togglePeriodicTable() {
    this.isPeriodicTableOpen = !this.isPeriodicTableOpen;
    this.showPeriodicTable.next(this.isPeriodicTableOpen);
  }

  closeSessionAnalytics() {
    this.showSessionAnalytics.next(false);
    this.isSessionAnalyticsOpen = false;
  }

  toggleSessionAnalytics() {
    this.isSessionAnalyticsOpen = !this.isSessionAnalyticsOpen;
    this.showSessionAnalytics.next(this.isSessionAnalyticsOpen);
  }

  toggleBreakoutRoomsManagement() {
    this.breakoutRoomsManagementService.showBreakoutRoomsManagementScreen();
  }
}
